import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';

import AppRoutes from './routes/AppRoutes';
import { UIProvider } from './shared/UI/Uicontext';
import GlobalFetchIndicator from './shared/components/Loading/GlobalFetchIndicator';
import { ThemeProvider } from './shared/UI/ThemeContext';
import { useAuthKey } from './store/api/authKey';
import { AppDispatch } from './store';
import { loadSession, clearSession } from './store/slices/sessionSlice';
import {
  decodeSessionFromStorage,
  isSessionExpired,
  refreshTokenExpiry,
} from './shared/services/userSessionService';
import { extractSsoPayload } from './shared/services/ssoService';
import { useSsoBootstrap } from './shared/hooks/useSsoBootstrap';
import { useIdleLogout } from './shared/hooks/useIdleLogout';

// localStorage keys cleared when the session has expired.
const AUTH_KEYS = [
  'token',
  'user_token',
  'AuthorizationKey',
  'MenuList',
  'ModList',
  'ReactMenus',
  'ReactMenuChildren',
  'currentAy',
  'username',
  'officeKeyID',
  'AUTH_BRIDGE_SYNC',
];

// How often to re-extend the token. This MUST be well shorter than the token's
// own lifetime: refreshTokenExpiry() bumps `exp` by +1h, but it only runs while
// the session is still valid (the guard below skips it once isSessionExpired()
// is true). This MUST stay well shorter than the token's own lifetime so the
// extension lands before the token can lapse — a cadence equal to the token
// lifetime (e.g. 1h) races expiry and the guard skips it, so the session dies.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function AppBootstrap() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // Is the app being opened via the Angular SSO handoff URL? Computed once.
  const hasSso = useMemo(() => !!extractSsoPayload(), []);

  // Runs the SSO exchange when a handoff payload is present; otherwise idle.
  const ssoStatus = useSsoBootstrap();

  // ── Mount: validate the session, then hydrate (non-SSO loads only) ──
  useEffect(() => {
    if (hasSso) return; // SSO flow sets the token itself.

    if (isSessionExpired()) {
      AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
      dispatch(clearSession());
      // Send the user to the login route (unless already there).
      if (window.location.pathname !== '/') navigate('/', { replace: true });
      return;
    }

    const session = decodeSessionFromStorage();
    if (session) dispatch(loadSession(session));
  }, [dispatch, navigate, hasSso]);

  // ── Sliding session: keep extending the token's exp while it's valid ──
  // Runs once on mount (so a tab reopened close to expiry is extended right
  // away) and then every REFRESH_INTERVAL_MS. The guard skips extension once
  // the token has actually lapsed — by then the user must re-authenticate.
  useEffect(() => {
    if (!isSessionExpired()) refreshTokenExpiry();
    const id = setInterval(() => {
      if (!isSessionExpired()) refreshTokenExpiry();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // ── Idle auto-logout: 15 min inactivity → 3s countdown → logout ──
  // Same clearing as before, but sent to the external TN EMIS site instead
  // of this app's own login route — window.location.href (not react-router's
  // navigate, which only handles routes inside this app) for the external
  // domain.
  const logout = useCallback(() => {
    AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
    dispatch(clearSession());
    window.location.href = 'https://tnemis.tnschools.gov.in';
  }, [dispatch]);

  const idleCountdown = useIdleLogout({
    idleMs: 15 * 60 * 1000, // 15 minutes of inactivity.
    countdownSeconds: 3,
    // Arm only when there's a session to protect — but on the login page there
    // is no token at all, so we also require one to exist. An EXPIRED token
    // still counts as "has a session" so idle logout fires (and clears it)
    // rather than silently leaving the user on a dead session.
    isActive: () => !!localStorage.getItem('token'),
    onLogout: logout,
  });

  // While the SSO handoff is being resolved, show a loader instead of routing
  // (the handoff URL matches no route and would otherwise flash a blank page).
  if (hasSso && (ssoStatus === 'idle' || ssoStatus === 'processing')) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: '100vh', gap: '1rem' }}
      >
        <ProgressSpinner />
        <span>Signing you in…</span>
      </div>
    );
  }

  return (
    <>
      <AppRoutes />
      {idleCountdown != null && <IdleCountdownModal seconds={idleCountdown} />}
    </>
  );
}

// Non-dismissable popup shown during the pre-logout countdown.
function IdleCountdownModal({ seconds }: { seconds: number }) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(17, 24, 39, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
          padding: '1.5rem',
          textAlign: 'center',
        }}
      >
        <i
          className="pi pi-clock"
          style={{
            fontSize: '2rem',
            color: 'var(--app-primary, #1e40af)',
            marginBottom: '0.75rem',
            display: 'block',
          }}
        />
        <h5 style={{ margin: '0 0 0.5rem', color: '#1f2937', fontWeight: 700 }}>
          Session Timeout
        </h5>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#4b5563' }}>
          You&apos;ve been idle. Logging out in{' '}
          <strong
            style={{ color: 'var(--app-primary, #1e40af)', fontSize: '1.1rem' }}
          >
            {seconds}
          </strong>
          …
        </p>
      </div>
    </div>
  );
}

function App() {
  useAuthKey();

  return (
    <ThemeProvider>
      <UIProvider>
        <GlobalFetchIndicator />
        <AppBootstrap />
      </UIProvider>
    </ThemeProvider>
  );
}

export default App;
